import { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { API_BASE } from "../utils/Instance";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function PerformanceHistory() {
  const [contestsByCategory, setContestsByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [performanceHistory, setPerformanceHistory] = useState([]);
  const [userPerformance, setUserPerformance] = useState({});
  const [performanceSummary, setPerformanceSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [selectedContestForResults, setSelectedContestForResults] = useState(null);
  const [contestResults, setContestResults] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [selectedVoterResult, setSelectedVoterResult] = useState(null);
  const { profile, refreshAccessToken } = useAuth();
  const navigate = useNavigate();

  // Helper function to make authenticated API calls
  const authenticatedFetch = async (url, options = {}) => {
    const token = profile?.accessToken;
    
    if (!token) {
      navigate("/login");
      return null;
    }

    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    };

    let response = await fetch(url, { ...options, headers });

    // If 401, try to refresh token and retry
    if (response.status === 401) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        headers.Authorization = `Bearer ${newToken}`;
        response = await fetch(url, { ...options, headers });
      } else {
        navigate("/login");
        return null;
      }
    }

    return response;
  };

  const formatNumber = (value) => Number(value || 0).toLocaleString("en-US");

  const formatAverage = (value) => {
    const numberValue = Number(value || 0);
    return numberValue.toLocaleString("en-US", {
      minimumFractionDigits: numberValue % 1 === 0 ? 0 : 1,
      maximumFractionDigits: 2,
    });
  };

  const normalizeVoters = (voters = []) =>
    voters.map((voter) => {
      if (typeof voter === "string") {
        return {
          screen_name: voter,
          total_votes: 1,
        };
      }

      return {
        screen_name: voter?.screen_name || voter?.name || voter?.username || "Unknown User",
        total_votes: voter?.total_votes || voter?.votes || 0,
      };
    });

  // Fetch contributor performance summary from real contest, vote, favorite, and view actions
  useEffect(() => {
    const fetchPerformanceSummary = async () => {
      try {
        setLoadingSummary(true);
        const response = await authenticatedFetch(`${API_BASE}accounts/profile/performance-summary/`);

        if (response && response.ok) {
          const data = await response.json();
          setPerformanceSummary(data);
          if (data.contests_joined_by_category) {
            setContestsByCategory(data.contests_joined_by_category);
          }
          if (Array.isArray(data.contest_performance_history)) {
            setPerformanceHistory(data.contest_performance_history);
            setUserPerformance(
              data.contest_performance_history.reduce((acc, contest) => {
                acc[contest.id] = {
                  position: contest.position ?? "—",
                  upvotes: contest.upvotes ?? 0,
                };
                return acc;
              }, {})
            );
          }
        }
      } catch (error) {
        console.error('Error fetching performance summary:', error);
      } finally {
        setLoadingSummary(false);
      }
    };

    if (profile?.accessToken) {
      fetchPerformanceSummary();
    } else {
      setLoadingSummary(false);
    }
  }, [profile?.accessToken]);

  // Fetch user's joined contests and count by category
  useEffect(() => {
    const fetchContestStats = async () => {
      try {
        setLoading(true);
        const token = profile?.accessToken;
        
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE}accounts/contests/my-contests/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          
          // Count contests by category
          const categoryCounts = data.reduce((acc, contest) => {
            const category = contest.category || "Other";
            acc[category] = (acc[category] || 0) + 1;
            return acc;
          }, {});
          
          setContestsByCategory(categoryCounts);
        }
      } catch (error) {
        console.error('Error fetching contest stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContestStats();
  }, [profile?.accessToken]);

  // Format category counts for display
  const formatCategoryCounts = () => {
    const categoryCounts = performanceSummary?.contests_joined_by_category || contestsByCategory;
    const categories = ["Full Body", "Chest & Stomach", "Butt", "P/V", "Legs", "Feet", "Teaser"];
    return categories
      .filter(cat => categoryCounts[cat] > 0)
      .map(cat => `${cat}: ${categoryCounts[cat]}`)
      .join(" | ") || "No contests joined yet";
  };

  const formatCategoryMetric = (metricKey, formatter = formatNumber) => {
    const metrics = performanceSummary?.category_metrics || [];
    const categoryDetails = metrics
      .filter((item) => Number(item?.[metricKey] || 0) > 0)
      .map((item) => `${item.category}: ${formatter(item[metricKey])}`);

    return categoryDetails.join(" | ") || "No category activity yet";
  };

  // Fetch contest results when "View" is clicked
  const handleViewContestResults = async (contest) => {
    setSelectedContestForResults(contest);
    setLoadingResults(true);
    setSelectedVoterResult(null);
    
    try {
      const response = await authenticatedFetch(
        `${API_BASE}accounts/votes/contest_results/?contest_id=${contest.id}`
      );
      
      if (response && response.ok) {
        const data = await response.json();
        console.log('Contest results:', data);
        setContestResults(data);
      } else {
        throw new Error('Failed to fetch contest results');
      }
    } catch (error) {
      console.error('Error fetching contest results:', error);
      setContestResults({ results: [] });
    } finally {
      setLoadingResults(false);
    }
  };

  // Close results modal
  const closeResultsModal = () => {
    setSelectedContestForResults(null);
    setContestResults(null);
    setSelectedVoterResult(null);
  };

  const handleViewMore = () => {
    // Add logic here if needed
  };

  const summaryCards = [
    {
      title: "Contests Joined",
      value: formatCategoryCounts(),
      detail: `${formatNumber(performanceSummary?.contests_joined_total)} total`,
    },
    {
      title: "Avg. Upvotes per Contest",
      value: formatAverage(performanceSummary?.avg_upvotes_per_contest),
      detail: formatCategoryMetric("avg_upvotes_per_contest", formatAverage),
    },
    {
      title: "Avg. Upvotes (Winners)",
      value: formatAverage(performanceSummary?.avg_upvotes_winning_contests),
      detail: formatCategoryMetric("avg_upvotes_winning_contests", formatAverage),
    },
    {
      title: "Best Individual Performance (Upvotes)",
      value: formatNumber(performanceSummary?.best_individual_upvotes),
      detail: formatCategoryMetric("best_individual_upvotes"),
    },
    {
      title: "Images Favorited",
      value: formatNumber(performanceSummary?.images_favorited),
      detail: formatCategoryMetric("images_favorited"),
    },
    {
      title: "Galleries Purchased",
      value: formatNumber(performanceSummary?.galleries_purchased ?? performanceSummary?.galleries_favorited),
      detail: formatCategoryMetric("galleries_purchased"),
    },
    {
      title: "Teaser Gallery Views",
      value: formatNumber(performanceSummary?.teaser_gallery_views),
      detail: "Teaser gallery opens",
    },
    {
      title: "Total Profile Visits",
      value: formatNumber(performanceSummary?.total_profile_visits),
      detail: "Contributor profile/detail opens",
    },
  ];

  const topContestPerformances = [...performanceHistory]
    .sort((a, b) => Number(b.upvotes || 0) - Number(a.upvotes || 0))
    .slice(0, 6);

  return (
    <div className="flex min-h-screen bg-black">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 lg:ml-64 w-full">
        <Topbar pageTitle="Performance History" />

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 mb-4">
          {summaryCards.map((card, idx) => (
            <div
              key={idx}
              className="bg-[#1C1C1E] rounded-2xl shadow-sm px-4 py-5 w-full"
            >
              <h2 className="text-lg font-bold text-[#ceb46a] mb-1">
                {card.title}
              </h2>
              <p className="text-white text-xl font-semibold">
                {loadingSummary ? "Loading..." : card.value}
              </p>
              {!loadingSummary && card.detail && (
                <p className="text-gray-400 text-xs mt-1">{card.detail}</p>
              )}
            </div>
          ))}
        </div>

        {/* 🟡 Recent Contest Performance Table */}
        <div className="py-6 md:py-5 overflow-x-auto">
          <h2 className="text-xl font-semibold mb-4 font-inter text-[#CEB46A]">
            Contest Performance History
          </h2>
          
          <div className="overflow-x-auto rounded-lg">
            <table className="w-full">
              <thead>
                <tr className="bg-[#ceb46a] text-black">
                  <th className="py-3 px-4 text-left">Gallery</th>
                  <th className="py-3 px-4 text-left">Contest Name</th>
                  <th className="py-3 px-4 text-left">Win Date</th>
                  <th className="py-3 px-4 text-left">Position</th>
                  <th className="py-3 px-4 text-left">Upvotes</th>
                  <th className="py-3 px-4 text-left">Full Results</th>
                </tr>
              </thead>
              <tbody>
                {performanceHistory.length > 0 ? (
                  performanceHistory.map((contest, index) => {
                    const performance = userPerformance[contest.id] || {
                      position: contest.position ?? "—",
                      upvotes: contest.upvotes ?? 0,
                    };
                    return (
                      <tr
                        key={contest.id}
                        className={
                          index % 2 === 0
                            ? "bg-[#161617] text-white"
                            : "bg-[#1C1C1E] text-white"
                        }
                      >
                        <td className="py-3 px-4">
                          <span className="font-medium text-white">
                            {contest.category || "General"}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-medium">{contest.title}</td>
                        <td className="py-3 px-4">
                          {contest.end_time 
                            ? new Date(contest.end_time).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })
                            : "N/A"}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`font-bold ${
                            performance.position === 1 ? 'text-yellow-400' :
                            performance.position === 2 ? 'text-gray-300' :
                            performance.position === 3 ? 'text-orange-400' :
                            'text-white'
                          }`}>
                            {performance.position}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-semibold">{performance.upvotes}</span>
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => handleViewContestResults(contest)}
                            className="px-4 py-2 rounded-lg font-semibold
                                       bg-[#ceb46a] text-black
                                       hover:bg-[#e3c97a] 
                                       transition duration-200 shadow-md
                                       hover:shadow-lg active:scale-95"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="py-6 text-center text-gray-400">
                      {loading ? "Loading..." : "No completed contests yet"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Results Modal */}
          {selectedContestForResults && (
            <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
              <div className="bg-[#1C1C1E] text-white rounded-2xl shadow-lg w-11/12 max-w-4xl p-6 max-h-[90vh] overflow-hidden">
                <h2 className="text-2xl font-bold text-[#ceb46a] mb-4">
                  Full Result – {selectedContestForResults.title}
                </h2>

                {loadingResults ? (
                  <div className="py-10 text-center text-gray-400">
                    Loading results...
                  </div>
                ) : (
                  <div className="overflow-y-auto max-h-[calc(90vh-180px)] hide-scrollbar">
                    <table className="w-full border-collapse">
                      <thead className="sticky top-0 bg-[#ceb46a] z-10">
                        <tr className="bg-[#ceb46a] text-black">
                          <th className="py-3 px-4 text-left">Place</th>
                          <th className="py-3 px-4 text-left">Contributor Screenname</th>
                          <th className="py-3 px-4 text-left">Total Votes</th>
                          <th className="py-3 px-4 text-left">Voter Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(contestResults?.results || []).length > 0 ? (
                          contestResults.results.map((result, idx) => {
                            const voters = normalizeVoters(result.voters || []);

                            return (
                              <tr
                                key={result.participant_id}
                                className={
                                  idx % 2 === 0 ? "bg-[#161617]" : "bg-[#1C1C1E]"
                                }
                              >
                                <td className="py-3 px-4 font-bold">
                                  {result.place}
                                </td>
                                <td className="py-3 px-4">{result.screen_name}</td>
                                <td className="py-3 px-4 font-semibold">{result.total_votes}</td>
                                <td className="py-3 px-4">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedVoterResult({
                                        contributor: result.screen_name,
                                        voters,
                                      });
                                    }}
                                    className="px-3 py-1 rounded bg-[#ceb46a] text-black font-semibold hover:bg-[#b99d55] transition duration-200"
                                  >
                                    View ({voters.length})
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="4" className="py-6 text-center text-gray-400">
                              No results available yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {selectedVoterResult && (
                  <div
                    className="fixed inset-0 z-[60] bg-black bg-opacity-70 flex items-center justify-center px-4"
                    onClick={() => setSelectedVoterResult(null)}
                  >
                    <div
                      className="bg-[#1C1C1E] border border-[#ceb46a] text-white rounded-lg shadow-2xl w-full max-w-lg p-6"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-start justify-between gap-4 mb-5">
                        <h3 className="text-xl font-bold text-[#ceb46a]">
                          Voters for {selectedVoterResult.contributor}
                        </h3>
                        <button
                          type="button"
                          onClick={() => setSelectedVoterResult(null)}
                          className="text-gray-400 hover:text-white text-2xl leading-none"
                          aria-label="Close voter details"
                        >
                          &times;
                        </button>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-[#ceb46a] text-black">
                              <th className="py-3 px-4 text-left">User Screenname</th>
                              <th className="py-3 px-4 text-right">Total Votes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedVoterResult.voters.length > 0 ? (
                              selectedVoterResult.voters.map((voter, voterIdx) => (
                                <tr
                                  key={`${voter.screen_name}-${voterIdx}`}
                                  className={voterIdx % 2 === 0 ? "bg-[#161617]" : "bg-[#232326]"}
                                >
                                  <td className="py-3 px-4">{voter.screen_name}</td>
                                  <td className="py-3 px-4 text-right font-semibold">
                                    {voter.total_votes}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="2" className="py-6 text-center text-gray-400">
                                  No voters yet
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end mt-6">
                  <button
                    onClick={closeResultsModal}
                    className="px-5 py-2 rounded-lg font-semibold
                               bg-[#ceb46a] text-black
                               hover:bg-[#e3c97a] transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* 🟡 View more button */}
        <div className="relative flex items-center justify-center w-full my-5 md:my-8">
          <div className="absolute w-full border-t border-[#ceb46a]"></div>
          <button
            onClick={handleViewMore}
            className="relative z-10 px-4 sm:px-6 py-2 bg-[#ceb46a] text-black rounded-full shadow text-sm md:text-base"
          >
            View more
          </button>
        </div>

        {/* Top contest performance from real contributor results */}
        <div className="">
          <h2 className="text-xl font-semibold mb-4 font-inter text-[#CEB46A]">
            Top Contest Performance
          </h2>
          {topContestPerformances.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {topContestPerformances.map((contest) => (
                <button
                  key={`${contest.id}-${contest.participant_id}`}
                  type="button"
                  onClick={() => handleViewContestResults(contest)}
                  className="text-left bg-[#1C1C1E] border border-[#2f2f32] hover:border-[#ceb46a] rounded-lg p-4 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[#ceb46a] font-semibold">
                        {contest.category || "General"}
                      </p>
                      <h3 className="text-white font-bold mt-1">
                        {contest.title}
                      </h3>
                    </div>
                    <span className="rounded-full bg-[#ceb46a] text-black text-xs font-bold px-3 py-1">
                      #{contest.position || "—"}
                    </span>
                  </div>
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <span className="text-gray-400">Upvotes</span>
                    <span className="text-white font-semibold">
                      {formatNumber(contest.upvotes)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="bg-[#1C1C1E] rounded-lg p-6 text-center text-gray-400">
              No completed contest performance yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
